import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserCard from '../../components/User';
import ErrorPage from '../../components/Error';
import Footer from '../../components/Footer';
import { FaArrowLeft } from 'react-icons/fa';
import { trackEvent } from '../../utils/analytics';
import { motion } from 'framer-motion';

const TeamMembersPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const suggestionsRef = useRef(null);

  const languages = [
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'es', label: 'ES', flag: '🇪🇸' },
    { code: 'it', label: 'IT', flag: '🇮🇹' },
    { code: 'de', label: 'DE', flag: '🇩🇪' },
    { code: 'zh', label: 'ZH', flag: '🇨🇳' },
    { code: 'ru', label: 'RU', flag: '🇷🇺' },
    { code: 'uk', label: 'UK', flag: '🇺🇦' },
    { code: 'pt', label: 'PT', flag: '🇵🇹' },
    { code: 'ko', label: 'KO', flag: '🇰🇷' },
    { code: 'ja', label: 'JA', flag: '🇯🇵' },
    { code: 'ar', label: 'AR', flag: '🇸🇦' },
    { code: 'sv', label: 'SV', flag: '🇸🇪' },
    { code: 'no', label: 'NO', flag: '🇳🇴' },
  ];

  const currentLang = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'fr';
  const currentFlag = languages.find(l => l.code === currentLang)?.flag || '🇫🇷';

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/Equipe/${id}`)
      .then(response => response.json())
      .then(data => {
        if (
          data.liste_membre.length === 1 &&
          data.liste_membre[0].Prenom === 'Equipe inexistante' &&
          data.liste_membre[0].Nom === 'Equipe inexistante' &&
          data.liste_membre[0].Lien === 'Equipe inexistante'
        ) {
          throw new Error('teamNotFound');
        }

        const payable = data.liste_membre.filter(m =>
          m.Prenom === "Faire dons a toute l'equipe" ||
          m.id ||
          (m.Lien && m.Lien.trim() !== '' && m.Lien !== 'Equipe inexistante')
        );
        setMembers(payable);
        setFilteredMembers(payable);
        localStorage.setItem("LastTeamId", id);

        if (data.liste_membre.length > 0) {
          setTeamName(data.liste_membre[0].Nom || '');
        }

        if (data.LinkAvis && data.LinkAvis.trim() !== "") {
          localStorage.setItem("LinkAvis", data.LinkAvis);
        } else {
          localStorage.removeItem("LinkAvis");
        }

        setLoading(false);
      })
      .catch(error => {
        setError(error.message);
        setLoading(false);
      });
  }, [id]);

  const normalizeString = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const handleSearch = (e) => {
    const query = normalizeString(e.target.value.trim());
    setSearchQuery(e.target.value);

    if (query === "") {
      setFilteredMembers(members);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const groupedMembers = {};

    members.forEach((member) => {
      const searchTarget =
        member.Prenom === "Faire dons a toute l'equipe"
          ? member.Nom
          : member.Prenom;

      const normalizedTarget = normalizeString(searchTarget);

      if (normalizedTarget.includes(query)) {
        if (!groupedMembers[searchTarget]) {
          groupedMembers[searchTarget] = [];
        }
        groupedMembers[searchTarget].push(member);
      }
    });

    const newSuggestions = Object.keys(groupedMembers);

    if (newSuggestions.length === 0) {
      setFilteredMembers([]);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSuggestions(newSuggestions.slice(0, 5));
    setFilteredMembers(Object.values(groupedMembers).flat());
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    const filtered = members.filter(
      (member) =>
        (member.Prenom === suggestion && member.Prenom !== "Faire dons a toute l'equipe") ||
        (member.Prenom === "Faire dons a toute l'equipe" && member.Nom === suggestion)
    );

    setSearchQuery(suggestion);
    setFilteredMembers(filtered);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleBack = () => {
    const newPath = location.pathname.replace('/members', '');
    navigate(newPath);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-modern flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-500 font-medium">{t('teamTitle')}</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage message={error} />;
  }

  return (
    <div className="relative no-scroll min-h-screen bg-modern text-black flex flex-col justify-between overflow-hidden">

      <div className="flex flex-col items-center min-h-screen w-full">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-screen bg-white rounded-b-[2rem] shadow-lg px-6 pt-10 pb-8 text-center mb-4 relative"
        >
          {/* Back button */}
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 bg-gray-100 hover:bg-gray-200 text-black p-2.5 rounded-full transition"
          >
            <FaArrowLeft size={16} />
          </button>

          {/* Language switcher */}
          <div className="absolute top-4 right-4" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-sm font-medium px-3 py-1.5 rounded-full transition"
            >
              <span>{currentFlag}</span>
              <span className="text-xs text-gray-600">{currentLang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-24 max-h-60 overflow-y-auto z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm transition ${
                      currentLang === lang.code
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <h1
            className="text-2xl md:text-3xl font-bold text-black cursor-pointer"
            onClick={handleBack}
          >
            {teamName || t('teamTitle')}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {t('contactless_tips')}
          </p>
        </motion.div>

        {/* Zone scrollable */}
        <div className="overflow-y-auto flex-1 w-full flex flex-col items-center">

          {/* Barre de recherche */}
          {members.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="w-full max-w-lg mt-6 mb-6 px-4 relative"
            >
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearch}
                className="w-full px-5 py-3 bg-white border border-gray-200 text-black rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition text-sm"
              />
              {/* Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <ul
                  ref={suggestionsRef}
                  className="absolute z-10 left-4 right-4 mt-2 py-2 bg-white border border-gray-200 rounded-2xl shadow-lg"
                >
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      className="px-5 py-2.5 cursor-pointer hover:bg-gray-50 text-black text-sm transition"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}

          {!members.length > 1 && <div className="py-6" />}

          {/* Grille des membres */}
          <div className="grid grid-cols-2 gap-4 sm:gap-5 pb-20 px-4 w-full max-w-2xl">
            {filteredMembers.length > 0 ? (
              filteredMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className={`flex justify-center ${filteredMembers.length === 1 ? 'col-span-2' : ''}`}
                >
                  <UserCard
                    prenom={member.Prenom}
                    nom={member.Nom}
                    lien={member.Lien}
                    photo={member.Photo}
                    photoType={member.PhotoType}
                    userId={member.id}
                    teamId={member.Prenom === "Faire dons a toute l'equipe" ? id : undefined}
                  />
                </motion.div>
              ))
            ) : (
              <p className="text-center text-lg text-gray-400 col-span-full py-12">
                {t('noResults')}
              </p>
            )}
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default TeamMembersPage;
